"use client";

import type { JSX } from "react";
import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { Popover, Transition } from "@headlessui/react";
import Link from "next/link";
import Image from "next/image";
import logo from "@/app/icon.png";
import config from "@/config";
import { categories } from "../content";
import { createClient } from "@/libs/supabase/client";
import { User } from "@supabase/supabase-js";
import ButtonSignin from "@/components/ButtonSignin";

const links: {
  href: string;
  label: string;
}[] = [
  {
    href: "/blog/",
    label: "All Posts",
  },
];


const ButtonPopoverCategories = () => {
  return (
    <Popover className="relative z-30">
      {({ open }) => (
        <>
          <Popover.Button
            className="link no-underline flex flex-nowrap items-center gap-1 text-base-content/80 hover:text-base-content active:text-base-content focus:text-base-content duration-100"
            title="Open Blog categories"
          >
            Categories
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className={`w-5 h-5 duration-200 ${
                open ? "transform rotate-180 " : ""
              }`}
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="absolute left-0 z-30 mt-3 w-screen max-w-full sm:max-w-sm transform">
              {({ close }) => (
                <div className="overflow-hidden rounded-box shadow-lg ring-1 ring-base-content ring-opacity-5">
                  <div className="relative grid gap-2 bg-base-100 p-2 overflow-hidden">
                    {categories.map((category) => (
                      <div key={category.slug} onClick={() => close()}>
                        <Link
                          className="block text-left p-3 -m-1 cursor-pointer hover:bg-base-200 rounded-box duration-200"
                          href={`/blog/category/${category.slug}`}
                        >
                          <div className="">
                            <p className="font-medium mb-0.5">
                              {category?.titleShort || category.title}
                            </p>
                            <p className="text-sm opacity-80">
                              {category?.descriptionShort ||
                                category.description}
                            </p>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

// This is the header that appears on all pages in the /blog folder.
// By default it shows the logo, the links, and the CTA.
// In the links, there's a popover with the categories.
const HeaderBlog = () => {
  const supabase = createClient();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, [supabase]);

  // setIsOpen(false) when the route changes (i.e: when the user clicks on a link on mobile)
  useEffect(() => {
    setIsOpen(false);
  }, [searchParams]);

  const cta: JSX.Element = (
    <div className="flex gap-2">
      <Link href="/service" className="btn btn-outline btn-secondary md:btn-sm">Done For You</Link>
      {user ? (
        <Link href="/dashboard" className="btn btn-outline btn-primary md:btn-sm">
          Dashboard
        </Link>
      ) : (
        <ButtonSignin text="Do It Yourself" extraStyle="btn-outline btn-primary md:btn-sm" />
      )}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 bg-base-100/95 backdrop-blur-sm border-b border-base-200">
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between h-16">
          {/* Your logo/name on large screens */}
          <div className="flex lg:flex-1">
            <Link
              className="flex items-center gap-2 shrink-0"
              href="/"
              title={`${config.appName} homepage`}
            >
              <Image
                src={logo}
                alt={`${config.appName} logo`}
                className="w-8 h-8 rounded-full object-cover"
                priority={true}
                width={32}
                height={32}
              />
              <span className="text-xl font-bold text-base-content hidden sm:inline">{config.appName}</span>
            </Link>
          </div>
          {/* Burger button to toggle menu on mobile */}
          <button
            type="button"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden btn btn-ghost btn-sm btn-square"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Your links on large screens */}
          <div className="hidden lg:flex lg:justify-center lg:gap-12 lg:items-center">
            {links.map((link) => (
              <Link
                href={link.href}
                key={link.href}
                className="link link-hover text-base-content/80 hover:text-base-content active:text-base-content focus:text-base-content duration-100"
                title={link.label}
              >
                {link.label}
              </Link>
            ))}

            <ButtonPopoverCategories />
          </div>

          {/* CTA on large screens */}
          <div className="hidden lg:flex lg:justify-end lg:flex-1">{cta}</div>
        </nav>
      </div>

      {/* Mobile Menu Dropdown */}
      {isOpen && (
        <div className="lg:hidden border-t border-base-200 bg-base-100/95 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-col gap-2">
              {links.map((link) => (
                <Link
                  href={link.href}
                  key={link.href}
                  className="btn btn-ghost btn-sm justify-start"
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {categories.map((category) => (
                <Link
                  href={`/blog/category/${category.slug}`}
                  key={category.slug}
                  className="btn btn-ghost btn-sm justify-start"
                  onClick={() => setIsOpen(false)}
                >
                  {category?.titleShort || category.title}
                </Link>
              ))}
              <div className="pt-2 mt-2 border-t border-base-200 flex flex-col gap-2">
                <Link
                  href="/service"
                  className="btn btn-outline btn-secondary btn-sm btn-block"
                  onClick={() => setIsOpen(false)}
                >
                  Done For You
                </Link>
                {user ? (
                  <Link
                    href="/dashboard"
                    className="btn btn-outline btn-primary btn-sm btn-block"
                    onClick={() => setIsOpen(false)}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <ButtonSignin text="Do It Yourself" extraStyle="btn-outline btn-primary btn-sm btn-block" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default HeaderBlog;